/* @jsx h */
import type { EpsonPrinterInfo } from "@app/vue/providers/bridge-rpc-types";
import { useLocalization } from "@lemmonhq/uikit";
import { defineComponent, h } from "vue";
import type { SetupError } from "./bluetoothPrinter";
import { PairingGuide, PairingVideo } from "./PrinterPairingGuide";
import { HeroIcon, StepHeader } from "./PrinterSetupLayout";
import { PasswordField } from "./PasswordField";

export { HeroIcon };
import type { EpsonPrinterStatus } from "@app/vue/providers/bridge-rpc-types";

export type ConfigureStage = "connect" | "status";

const CONFIGURE_STAGES: ConfigureStage[] = ["connect", "status"];

export const SetupErrorMessage = defineComponent<{ error: SetupError }>({
  name: "PrinterSetupErrorMessage",
  props: ["error"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="mt-4 w-full max-w-sm rounded-2xl border border-error/30 bg-error/10 p-4 text-left text-sm text-error">
        <div class="flex gap-2">
          <span class="material-symbols-outlined">error</span>
          <span>{t(`devices.bt_printer.err_${props.error.kind}`)}</span>
        </div>
        {props.error.detail && (
          <div class="mt-2 break-words font-mono text-xs text-error/70">
            {props.error.detail}
          </div>
        )}
      </div>
    );
  },
});

export const WelcomeStep = defineComponent<{ hasBridge: boolean }>({
  name: "PrinterSetupWelcomeStep",
  props: ["hasBridge"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
        <HeroIcon icon="print" />
        <StepHeader
          title={t("devices.bt_printer.welcome_title")}
          text={t("devices.bt_printer.welcome_desc")}
        />
        {!props.hasBridge && (
          <div class="w-full rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
            <span class="material-symbols-outlined mr-2 align-middle">
              info
            </span>
            {t("devices.bt_printer.native_required")}
          </div>
        )}
      </div>
    );
  },
});

export const DiscoverStep = defineComponent<{
  isSearching: boolean;
  searchAttempted: boolean;
  printers: EpsonPrinterInfo[];
  error: SetupError | null;
  onSelect: (printer: EpsonPrinterInfo) => void;
}>({
  name: "PrinterSetupDiscoverStep",
  props: ["isSearching", "searchAttempted", "printers", "error", "onSelect"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
        {props.printers.length === 0 && <PairingVideo />}
        <HeroIcon
          icon={props.isSearching ? "bluetooth_searching" : "bluetooth"}
          isAnimating={props.isSearching}
        />
        <StepHeader
          title={
            props.isSearching
              ? t("devices.bt_printer.searching")
              : t("devices.bt_printer.find_title")
          }
          text={
            props.isSearching
              ? t("devices.bt_printer.searching_desc")
              : t("devices.bt_printer.search_hint")
          }
        />

        {!props.isSearching && props.printers.length > 0 && (
          <div class="w-full max-w-sm space-y-2">
            <p class="mb-3 text-xs font-semibold uppercase tracking-wider text-base-content/50">
              {t("devices.bt_printer.available")}
            </p>
            {props.printers.map((printer) => (
              <button
                key={printer.target}
                onClick={() => props.onSelect(printer)}
                class="flex w-full items-center gap-3 rounded-xl bg-base-200 px-3 py-2.5 text-left ring-1 ring-base-content/5 transition-all hover:ring-primary/50"
              >
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <span class="material-symbols-outlined text-2xl">print</span>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="truncate text-base font-semibold text-accent">
                    {printer.deviceName}
                  </div>
                  <div class="truncate font-mono text-xs text-base-content/50">
                    {printer.target}
                  </div>
                </div>
                <span class="material-symbols-outlined text-xl text-base-content/30">
                  chevron_right
                </span>
              </button>
            ))}
          </div>
        )}

        {!props.isSearching &&
          props.searchAttempted &&
          !props.error &&
          props.printers.length === 0 && (
            <div class="mb-3 flex w-full max-w-sm items-center gap-3 rounded-xl bg-warning/10 px-3 py-2 text-left">
              <span class="material-symbols-outlined text-warning">
                bluetooth_disabled
              </span>
              <div>
                <p class="text-sm font-semibold text-accent">
                  {t("devices.bt_printer.none_found")}
                </p>
                <p class="text-xs text-base-content/60">
                  {t("devices.bt_printer.none_found_hint")}
                </p>
              </div>
            </div>
          )}

        {props.error && <SetupErrorMessage error={props.error} />}

        {props.printers.length === 0 && <PairingGuide />}
      </div>
    );
  },
});

export const ConfiguringStep = defineComponent<{
  printerName: string;
  stage: ConfigureStage;
  error: SetupError | null;
}>({
  name: "PrinterSetupConfiguringStep",
  props: ["printerName", "stage", "error"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const currentIndex = CONFIGURE_STAGES.indexOf(props.stage);
      return (
        <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
          <HeroIcon
            icon={
              props.error
                ? "error"
                : props.stage === "status"
                  ? "sync"
                  : "cable"
            }
            isAnimating={!props.error}
            isError={!!props.error}
          />
          <StepHeader
            title={
              props.error
                ? t("devices.bt_printer.setup_failed")
                : t("devices.bt_printer.setting_up")
            }
            text="{name}"
            values={{ name: props.printerName }}
          />

          {props.error ? (
            <SetupErrorMessage error={props.error} />
          ) : (
            <div class="w-full max-w-xs space-y-2">
              {CONFIGURE_STAGES.map((stage, stageIndex) => {
                const isDone = stageIndex < currentIndex;
                const isActive = props.stage === stage;
                return (
                  <div
                    key={stage}
                    class={[
                      "flex items-center gap-3 rounded-xl px-3 py-2 ring-1 transition-all",
                      isDone
                        ? "bg-success/10 ring-success/20"
                        : isActive
                          ? "bg-primary/10 ring-primary/30"
                          : "bg-base-200 ring-base-content/5",
                    ].join(" ")}
                  >
                    <div
                      class={[
                        "flex h-8 w-8 items-center justify-center rounded-full",
                        isDone
                          ? "bg-success/20 text-success"
                          : isActive
                            ? "bg-primary/20 text-primary"
                            : "bg-base-300 text-base-content/40",
                      ].join(" ")}
                    >
                      {isDone ? (
                        <span class="material-symbols-outlined text-lg">
                          check
                        </span>
                      ) : isActive ? (
                        <span class="loading loading-spinner loading-sm" />
                      ) : (
                        <span class="material-symbols-outlined text-lg">
                          more_horiz
                        </span>
                      )}
                    </div>
                    <span
                      class={[
                        "text-sm font-medium",
                        isDone
                          ? "text-success"
                          : isActive
                            ? "font-semibold text-accent"
                            : "text-base-content/40",
                      ].join(" ")}
                    >
                      {t(`devices.bt_printer.stage_${stage}`)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    };
  },
});

export const PasswordStep = defineComponent<{
  printerName: string;
  password: string;
  disabled: boolean;
  error: SetupError | null;
  onInput: (value: string) => void;
  onSubmit: () => void;
}>({
  name: "PrinterSetupPasswordStep",
  props: [
    "printerName",
    "password",
    "disabled",
    "error",
    "onInput",
    "onSubmit",
  ],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
        <HeroIcon icon="key" isError={!!props.error} />
        <StepHeader
          title={t("devices.bt_printer.password_title")}
          text={t("devices.bt_printer.password_desc")}
          values={{ name: props.printerName }}
        />
        <form
          class="w-full max-w-sm"
          onSubmit={(event: Event) => {
            event.preventDefault();
            props.onSubmit();
          }}
        >
          <label class="form-control w-full text-left">
            <span class="label-text mb-1">
              {t("devices.bt_printer.password_label")}
            </span>
            <PasswordField
              placeholder={t("devices.bt_printer.password_placeholder")}
              value={props.password}
              disabled={props.disabled}
              onInput={props.onInput}
            />
          </label>
        </form>
        {props.error && <SetupErrorMessage error={props.error} />}
      </div>
    );
  },
});

const PAPER_LABEL: Record<EpsonPrinterStatus["paper"], string> = {
  OK: "devices.bt_printer.paper_ok",
  NEAR_END: "devices.bt_printer.paper_near_end",
  EMPTY: "devices.bt_printer.paper_empty",
};

export const CompleteStep = defineComponent<{
  printerName: string;
  status: EpsonPrinterStatus | null;
  isPrinting: boolean;
  error: SetupError | null;
  onTestPrint: () => void;
}>({
  name: "PrinterSetupCompleteStep",
  props: ["printerName", "status", "isPrinting", "error", "onTestPrint"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
        <HeroIcon icon="check_circle" isSuccess />
        <StepHeader
          title={t("devices.bt_printer.ready_title")}
          text={t("devices.bt_printer.ready_desc")}
          values={{ name: props.printerName }}
        />

        {props.status && (
          <div class="w-full max-w-sm rounded-2xl bg-base-200 p-4 ring-1 ring-base-content/5">
            <div class="flex items-center justify-center gap-6 text-xs">
              <div class="flex items-center gap-2">
                <span
                  class={[
                    "h-2 w-2 rounded-full",
                    props.status.connection
                      ? "bg-success"
                      : "bg-base-content/30",
                  ].join(" ")}
                />
                <span
                  class={
                    props.status.connection
                      ? "text-success"
                      : "text-base-content/50"
                  }
                >
                  {props.status.connection
                    ? t("devices.core.connected")
                    : t("devices.core.disconnected")}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class={[
                    "h-2 w-2 rounded-full",
                    props.status.paper === "OK"
                      ? "bg-success"
                      : props.status.paper === "NEAR_END"
                        ? "bg-warning"
                        : "bg-error",
                  ].join(" ")}
                />
                <span
                  class={
                    props.status.paper === "OK"
                      ? "text-success"
                      : "text-warning"
                  }
                >
                  {t(PAPER_LABEL[props.status.paper])}
                </span>
              </div>
            </div>
            {props.status.coverOpen && (
              <p class="mt-3 text-xs text-warning">
                {t("devices.bt_printer.cover_open")}
              </p>
            )}
            {props.status.error && (
              <p class="mt-3 text-xs text-warning">{props.status.error}</p>
            )}
          </div>
        )}

        <button
          onClick={props.onTestPrint}
          disabled={props.isPrinting}
          class="btn btn-outline btn-sm mt-4"
        >
          {props.isPrinting ? (
            <span class="loading loading-spinner loading-sm" />
          ) : (
            <span class="material-symbols-outlined">receipt_long</span>
          )}
          {t("devices.core.print_test_receipt")}
        </button>

        {props.error && <SetupErrorMessage error={props.error} />}
      </div>
    );
  },
});
